import { createContext, useContext } from 'react';
import { toRosterSport } from '../../constants/sport';
import { useAuth } from '../../hooks/useAuth';
import type { DirectorySport } from '../../types/directory';

export interface DirectorySportState {
  /** The sport the directory pages read: a system admin's pick, else the organization's. */
  sport: DirectorySport;
  /** True for a system admin only: the Baseball / Softball switch is offered. */
  canSwitch: boolean;
  /** Picks the sport (a no-op for anyone who cannot switch). */
  setSport: (sport: DirectorySport) => void;
}

export const DirectorySportContext = createContext<DirectorySportState | null>(null);

/** The organization's sport, impersonation first (see "Sport" in web/CLAUDE.md). */
export const useOrganizationSport = (): DirectorySport => {
  const { activeOrganization, user } = useAuth();
  return toRosterSport(activeOrganization ? activeOrganization.sport : user?.profile?.sport);
};

const noop = () => {};

/**
 * The sport every Recruiting Directory page reads. A coach always gets their program's sport; a
 * system admin gets the Baseball / Softball switch's pick. Outside the provider (tests, an isolated
 * render) it falls back to the organization's sport with no switch.
 */
export const useDirectorySport = (): DirectorySportState => {
  const context = useContext(DirectorySportContext);
  const organizationSport = useOrganizationSport();
  return context ?? { sport: organizationSport, canSwitch: false, setSport: noop };
};

/**
 * The switch's state without the organization fallback, so it never needs an auth provider: null
 * outside `DirectorySportProvider`. The switch, the URL sync and the admin views read this.
 */
export const useDirectorySportChoice = (): DirectorySportState | null => useContext(DirectorySportContext);
